import { useMemo, useState } from "react";
import { Plane, Car, Ship, Leaf, TreePine, ArrowRight, Mail, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * Carbon footprint calculator — Air / Road / Boat & Cruise.
 * Emission factors follow DEFRA/ICAO-style passenger averages (kg CO2e).
 * Purely client-side; nudges guests toward MyEcoTour's carbon-offset promise.
 */
const MODES = {
    air: {
        icon: Plane, label: "By Air", unit: "km flown",
        options: [
            { key: "economy", label: "Economy", factor: 0.158 },
            { key: "premium", label: "Premium economy", factor: 0.25 },
            { key: "business", label: "Business / First", factor: 0.31 },
        ],
        optionLabel: "Cabin class", perPax: true, hint: "Delhi → Dubai ≈ 2,200 km · Mumbai → London ≈ 7,200 km",
    },
    road: {
        icon: Car, label: "By Road", unit: "km driven",
        options: [
            { key: "car", label: "Car (petrol/diesel)", factor: 0.171, perVehicle: true },
            { key: "suv", label: "SUV / 4x4", factor: 0.22, perVehicle: true },
            { key: "ev", label: "Electric car", factor: 0.05, perVehicle: true },
            { key: "coach", label: "Bus / Coach", factor: 0.027 },
        ],
        optionLabel: "Vehicle", perPax: true, hint: "Mumbai → Goa ≈ 590 km · Delhi → Jaipur ≈ 280 km",
    },
    boat: {
        icon: Ship, label: "By Boat / Cruise", unit: "nights aboard",
        options: [
            { key: "cruise", label: "Ocean cruise (per night)", factor: 250, perNight: true },
            { key: "expedition", label: "Expedition ship (per night)", factor: 320, perNight: true },
            { key: "ferry", label: "Ferry (per km)", factor: 0.019, perKm: true },
        ],
        optionLabel: "Vessel", perPax: true, hint: "A 7-night cruise · or Havelock ferry ≈ 70 km",
    },
};

const fmtCO2 = (kg) => (kg >= 1000 ? `${(kg / 1000).toFixed(2)} tonnes` : `${Math.round(kg).toLocaleString()} kg`);

export const CarbonCalculator = () => {
    const [mode, setMode] = useState("air");
    const [option, setOption] = useState("economy");
    const [amount, setAmount] = useState("");
    const [travellers, setTravellers] = useState(2);
    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const m = MODES[mode];
    const opt = m.options.find((o) => o.key === option) || m.options[0];
    const isNights = !!opt.perNight;

    const result = useMemo(() => {
        const qty = parseFloat(amount);
        const pax = Math.max(1, parseInt(travellers, 10) || 1);
        if (!qty || qty <= 0) return null;
        let kg;
        if (opt.perVehicle) kg = qty * opt.factor; // whole vehicle, shared
        else kg = qty * opt.factor * pax;
        const perPerson = opt.perVehicle ? kg / pax : kg / pax;
        const trees = Math.max(1, Math.round(kg / 21)); // ~21 kg CO2 absorbed per tree per year
        return { total: kg, perPerson, trees };
    }, [amount, travellers, opt]);

    const pick = (key) => {
        setMode(key);
        setOption(MODES[key].options[0].key);
        setAmount("");
        setSent(false);
    };

    const emailResult = async (e) => {
        e.preventDefault();
        if (!result) return;
        setSending(true);
        try {
            await api.post("/carbon/email-result", {
                email,
                mode_label: m.label,
                option_label: opt.label,
                amount: parseFloat(amount),
                unit: isNights ? "nights" : "km",
                travellers: Math.max(1, parseInt(travellers, 10) || 1),
                total_kg: result.total,
                per_person_kg: result.perPerson,
                trees: result.trees,
            });
            setSent(true);
            toast.success("Estimate sent — check your inbox.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send just now — please try again shortly.");
        } finally {
            setSending(false);
        }
    };

    const inputCls = "w-full bg-transparent border-b border-primary-foreground/30 text-primary-foreground placeholder:text-primary-foreground/40 py-3 px-1 text-sm focus:outline-none focus:border-[#d8b45a] transition-colors";

    return (
        <section className="section-padding bg-primary text-primary-foreground grain border-t border-primary-foreground/10" data-testid="carbon-calculator-section">
            <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
                <div className="lg:col-span-5">
                    <p className="overline text-[#d8b45a] mb-4 inline-flex items-center gap-2"><Leaf size={13} /> Travel honestly</p>
                    <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight">What does your journey cost the sky?</h2>
                    <p className="mt-5 text-primary-foreground/70 leading-relaxed text-sm">
                        Estimate the carbon footprint of any leg of your travels — by air, road, or sea. Every journey booked
                        with MyEcoTour offsets its full footprint through verified reforestation and community projects, at no
                        extra cost to you.
                    </p>
                    <Link to="/impact" className="mt-7 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase font-semibold text-[#d8b45a] hover:text-primary-foreground transition-colors" data-testid="carbon-impact-link">
                        See our impact report <ArrowRight size={13} />
                    </Link>
                </div>

                <div className="lg:col-span-7 bg-primary-foreground/[0.04] border border-primary-foreground/15 p-7 sm:p-9" data-testid="carbon-calculator">
                    {/* Mode tabs */}
                    <div className="grid grid-cols-3 gap-2 mb-8">
                        {Object.entries(MODES).map(([key, cfg]) => {
                            const Icon = cfg.icon;
                            const active = mode === key;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => pick(key)}
                                    data-testid={`carbon-mode-${key}`}
                                    aria-pressed={active}
                                    className={`flex flex-col items-center gap-2 py-4 border text-[10px] tracking-[0.2em] uppercase font-semibold transition-colors ${active ? "border-[#d8b45a] bg-[#d8b45a]/10 text-[#d8b45a]" : "border-primary-foreground/20 text-primary-foreground/60 hover:border-primary-foreground/50"}`}
                                >
                                    <Icon size={18} strokeWidth={1.5} /> {cfg.label}
                                </button>
                            );
                        })}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        <label className="block">
                            <span className="text-[10px] tracking-[0.2em] uppercase text-primary-foreground/55">{m.optionLabel}</span>
                            <select value={option} onChange={(e) => setOption(e.target.value)} className={`${inputCls} cursor-pointer [&>option]:text-foreground`} data-testid="carbon-option">
                                {m.options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-[10px] tracking-[0.2em] uppercase text-primary-foreground/55">{isNights ? "Nights aboard" : "Distance (km)"}</span>
                            <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={isNights ? "7" : "2200"} className={inputCls} data-testid="carbon-amount" />
                        </label>
                        <label className="block">
                            <span className="text-[10px] tracking-[0.2em] uppercase text-primary-foreground/55">Travellers</span>
                            <input type="number" min="1" max="20" value={travellers} onChange={(e) => setTravellers(e.target.value)} className={inputCls} data-testid="carbon-travellers" />
                        </label>
                    </div>
                    <p className="mt-3 text-[11px] text-primary-foreground/45 italic">{m.hint}</p>

                    {/* Result */}
                    {result ? (
                        <div className="mt-8 pt-7 border-t border-primary-foreground/15 grid grid-cols-1 sm:grid-cols-3 gap-6 items-center" data-testid="carbon-result">
                            <div>
                                <p className="font-serif text-4xl text-[#d8b45a]" data-testid="carbon-total">{fmtCO2(result.total)}</p>
                                <p className="text-[10px] tracking-[0.2em] uppercase text-primary-foreground/55 mt-1">CO₂e · whole party</p>
                            </div>
                            <div>
                                <p className="font-serif text-2xl text-primary-foreground">{fmtCO2(result.perPerson)}</p>
                                <p className="text-[10px] tracking-[0.2em] uppercase text-primary-foreground/55 mt-1">per traveller</p>
                            </div>
                            <div className="flex items-start gap-3">
                                <TreePine size={22} strokeWidth={1.5} className="text-[#d8b45a] shrink-0 mt-0.5" />
                                <p className="text-xs text-primary-foreground/75 leading-relaxed" data-testid="carbon-trees">
                                    ≈ <span className="font-semibold text-primary-foreground">{result.trees.toLocaleString()} trees</span> growing
                                    for a year to absorb it — <span className="text-[#d8b45a]">we offset this free with every booking.</span>
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-8 pt-7 border-t border-primary-foreground/15 text-sm text-primary-foreground/50" data-testid="carbon-empty">
                            Enter a {isNights ? "number of nights" : "distance"} to see your estimate.
                        </div>
                    )}

                    {result && (
                        sent ? (
                            <p className="mt-6 text-sm text-[#d8b45a]" data-testid="carbon-email-sent">
                                Sent — your estimate is in your inbox, with our offset promise attached.
                            </p>
                        ) : (
                            <form onSubmit={emailResult} className="mt-6 flex flex-col sm:flex-row gap-3" data-testid="carbon-email-form">
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@email.com"
                                    className={`${inputCls} sm:flex-1`}
                                    data-testid="carbon-email-input"
                                />
                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#d8b45a] text-[#d8b45a] text-[10px] tracking-[0.25em] uppercase font-semibold hover:bg-[#d8b45a] hover:text-[#1d2b23] transition-colors disabled:opacity-50"
                                    data-testid="carbon-email-btn"
                                >
                                    {sending ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Email me this result
                                </button>
                            </form>
                        )
                    )}
                    <p className="mt-6 text-[10px] text-primary-foreground/40 leading-relaxed">
                        Estimates use DEFRA/ICAO-style passenger averages and are indicative, not exact. Cruise figures are per
                        passenger-night; car figures are per vehicle, shared across your party.
                    </p>
                </div>
            </div>
        </section>
    );
};
